import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  ChevronRight,
  Smartphone,
  Bluetooth,
  Palette,
  Zap,
  Sparkles,
  Package,
  BadgeCheck,
} from 'lucide-react';
import ColorAmbient from '../components/ColorAmbient';
import RegistrationMark from '../components/RegistrationMark';

const ACCENT = '#FF6B35';

const HIGHLIGHTS = [
  {
    title: 'Pocket-sized precision',
    desc:
      'Fits in a shirt pocket. Wireless, rechargeable and calibrated at the factory — no dark room required.',
    Icon: Smartphone,
  },
  {
    title: 'Own light source',
    desc:
      "Every measurement is taken with the sensor's own D50 LED illuminant, so ambient light never distorts your reading.",
    Icon: Zap,
  },
  {
    title: 'Bluetooth to phone or tablet',
    desc:
      'Pair with the free Nix Toolkit iOS / Android app. Save unlimited scans, palettes and projects to the cloud.',
    Icon: Bluetooth,
  },
  {
    title: 'Every color system',
    desc:
      'Instant conversion to Lab, LCh, sRGB, HEX, CMYK, LRV, Munsell — and matched against Pantone, RAL, Sherwin-Williams, Benjamin Moore and 80+ fan decks.',
    Icon: Palette,
  },
  {
    title: 'Rugged & repeatable',
    desc:
      "Machined aluminum body. Inter-unit agreement ΔE00 < 0.5 — the same reading whether it's -10°C on a job site or +40°C in a factory.",
    Icon: BadgeCheck,
  },
  {
    title: 'Made for the field',
    desc:
      'Print floors, paint stores, automotive shops, construction sites, textile mills, dental labs — anywhere color matters.',
    Icon: Package,
  },
];

const DEVICES = [
  {
    id: 'mini-3',
    name: 'Nix Mini 3',
    tag: 'Everyday',
    price: '$99',
    desc:
      'The world&apos;s smallest color-matching sensor. Instantly convert any physical color to digital values on your phone.',
    for: 'Designers · Content creators · Paint stores',
  },
  {
    id: 'pro-2',
    name: 'Nix Pro 2',
    tag: 'Professional',
    price: '$349',
    desc:
      'Two apertures (2 mm & 15 mm), higher accuracy, and a professional-grade CIE Lab / LCh readout for QC teams.',
    for: 'QC · Design agencies · Textile',
  },
  {
    id: 'spectro-2',
    name: 'Nix Spectro 2',
    tag: 'Spectrophotometer',
    price: '$1,199',
    desc:
      'A true handheld spectrophotometer. Full visible spectrum data, 45°/0° geometry, ΔE00 accuracy for coatings, plastics & print.',
    for: 'Print · Coatings · Plastics · Pantone matching',
  },
  {
    id: 'spectro-l',
    name: 'Nix Spectro L',
    tag: 'Large aperture',
    price: '$1,499',
    desc:
      'The Spectro 2 optical bench with a 15 mm aperture — perfect for textiles, carpets and rougher substrates.',
    for: 'Textile · Décor · Automotive interior',
  },
];

const USE_CASES = [
  {
    title: 'Print & packaging QC',
    desc: 'Verify spot colors, brand tolerances and ΔE targets right on the press.',
    color: '#FF6B35',
  },
  {
    title: 'Paint & coatings',
    desc: 'Match any real-world color to a fan deck or a customer target. Sell more, waste less.',
    color: '#0A2A63',
  },
  {
    title: 'Textile & décor',
    desc: 'On-fabric measurement without a lab — production line to design studio in one scan.',
    color: '#E5007E',
  },
  {
    title: 'Field service',
    desc: 'Automotive, dental, cosmetics, construction — pocket color science, wherever you go.',
    color: '#00B7C7',
  },
];

const NixPage = () => {
  return (
    <main
      className="pt-28 pb-24 bg-white min-h-screen relative overflow-hidden"
      data-testid="nix-page"
    >
      <ColorAmbient variant="warm" intensity="medium" />
      <RegistrationMark className="absolute top-24 left-6 opacity-40" size={24} />
      <RegistrationMark className="absolute top-24 right-6 opacity-40" size={24} />

      {/* Hero */}
      <section className="container-pn relative">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur border border-[#0A2A63]/10 text-[#0A2A63] mb-6 shadow-sm"
              data-testid="nix-eyebrow"
            >
              <Sparkles className="h-3.5 w-3.5" style={{ color: ACCENT }} />
              <span className="text-xs font-semibold tracking-widest uppercase">
                Brand Partner · Hamilton, Canada
              </span>
            </div>
            <img
              src="/logos/nix.png"
              alt="Nix Sensor"
              className="h-14 mb-6 object-contain object-left"
            />
            <h1
              className="text-5xl md:text-7xl font-black text-[#050F2E] tracking-tight leading-[0.95]"
              data-testid="nix-headline"
            >
              Pocket-sized{' '}
              <span className="pn-gradient-text">color science.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Nix Sensor makes the smallest color-matching instruments on the planet. A whole
              color lab that fits in your hand, pairs to your phone, and turns any surface into
              measurable Lab, HEX, CMYK, sRGB, LRV — or a Pantone / RAL match.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/contact">
                <Button
                  data-testid="nix-cta-demo"
                  className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-12 px-7 font-bold btn-transition"
                >
                  Request a demo <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <Link to="/shop">
                <Button
                  data-testid="nix-cta-shop"
                  variant="outline"
                  className="rounded-full h-12 px-7 font-bold border-2 border-[#0A2A63] text-[#0A2A63] hover:bg-[#0A2A63] hover:text-white btn-transition bg-white/70 backdrop-blur"
                >
                  Shop Nix devices
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="mt-14 grid grid-cols-3 max-w-lg gap-4">
              <div>
                <p className="text-3xl md:text-4xl font-black text-[#0A2A63]">200k+</p>
                <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">
                  Sensors shipped
                </p>
              </div>
              <div className="border-l border-slate-200 pl-4">
                <p className="text-3xl md:text-4xl font-black text-[#0A2A63]">80+</p>
                <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">
                  Fan-deck libraries
                </p>
              </div>
              <div className="border-l border-slate-200 pl-4">
                <p className="text-3xl md:text-4xl font-black text-[#0A2A63]">ΔE&lt;0.5</p>
                <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">
                  Inter-unit accuracy
                </p>
              </div>
            </div>
          </div>

          {/* Right visual — real Nix Spectro 2 product photo */}
          <div className="lg:col-span-5 relative h-[520px]">
            <div className="absolute inset-0 rounded-[28px] overflow-hidden shadow-2xl bg-gradient-to-br from-[#050F2E] via-[#0A2A63] to-[#FF6B35]">
              <img
                src="/nix/spectro2-lifestyle.jpg"
                alt="Nix Spectro 2 measuring a print chart, with real-time Lab data on the paired phone app"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="nix-hero-photo"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050F2E]/70 via-transparent to-transparent" />

              <div className="absolute top-5 left-5 bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-xl">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Model</p>
                <p className="text-lg font-black text-[#0A2A63]">Nix Spectro 2</p>
              </div>
              <div className="absolute top-5 right-5 bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-xl">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Weight</p>
                <p className="text-2xl font-black text-[#0A2A63]">
                  38<span className="text-[#FF6B35] text-base">g</span>
                </p>
              </div>
              <div className="absolute bottom-6 left-6 right-6 flex flex-wrap gap-2">
                {['CIE Lab', 'HEX', 'CMYK', 'LRV', 'Pantone', 'RAL'].map((c) => (
                  <span
                    key={c}
                    className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-white/95 text-[#0A2A63] shadow-md"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 h-24 w-24 rounded-full border-2 border-dashed border-[#FF4E50] animate-spin-slow opacity-70" />
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="container-pn mt-28 relative" data-testid="nix-highlights">
        <div className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">
            Why Nix
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-[#050F2E] tracking-tight">
            A color lab in your pocket.
          </h2>
          <p className="mt-4 text-slate-600">
            Nix built the first mass-market color sensor that fits on a keychain — and then made
            it accurate enough for Fortune-500 QC teams. Here&apos;s what makes it special.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {HIGHLIGHTS.map(({ title, desc, Icon }, idx) => (
            <div
              key={title}
              data-testid={`nix-highlight-${idx}`}
              className="group bg-white rounded-2xl p-7 border border-slate-100 hover:border-[#FF6B35] hover:shadow-2xl btn-transition"
            >
              <div className="flex items-center justify-between mb-4">
                <span
                  className="h-11 w-11 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(255,107,53,0.14)' }}
                >
                  <Icon className="h-5 w-5" style={{ color: ACCENT }} />
                </span>
                <span className="text-xs font-mono text-slate-300">0{idx + 1}</span>
              </div>
              <h3 className="text-xl font-bold text-[#050F2E]">{title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Device lineup */}
      <section className="container-pn mt-28 relative" data-testid="nix-lineup">
        <div className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">The lineup</p>
          <h2 className="text-3xl md:text-4xl font-black text-[#050F2E] tracking-tight">
            Four sensors. One color language.
          </h2>
          <p className="mt-4 text-slate-600">
            From a keychain color-matcher to a full handheld spectrophotometer — every Nix speaks
            the same data format, so your team can grow into the next tier without retraining.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {DEVICES.map((d) => (
            <div
              key={d.id}
              data-testid={`nix-device-${d.id}`}
              className="bg-white rounded-2xl border-2 border-slate-100 p-6 hover:border-[#FF6B35] hover:shadow-xl btn-transition flex flex-col"
            >
              <div className="flex items-center justify-between mb-4">
                <span
                  className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full text-white"
                  style={{ backgroundColor: ACCENT }}
                >
                  {d.tag}
                </span>
                <span className="text-sm font-black text-[#0A2A63]">{d.price}</span>
              </div>
              <h3 className="text-lg font-black text-[#050F2E]">{d.name}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed flex-1">{d.desc}</p>
              <p className="mt-4 border-t border-slate-100 pt-3 text-[11px] uppercase tracking-widest text-slate-500 font-bold">
                For
              </p>
              <p className="text-xs text-[#050F2E] font-semibold">{d.for}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Use cases ribbon */}
      <section className="container-pn mt-24 relative" data-testid="nix-usecases">
        <div className="grid lg:grid-cols-12 gap-10 items-center bg-[#0A2A63] text-white rounded-3xl p-10 md:p-14 shadow-2xl overflow-hidden relative">
          <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-[#FF6B35]/25 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#FF4E50]/15 blur-3xl" />

          <div className="lg:col-span-5 relative">
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">
              Where it&apos;s used
            </p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight leading-tight">
              Trusted by brands from paint chains to Fortune-500 QC labs.
            </h2>
            <p className="mt-4 text-slate-300">
              Nix instruments are used by Sherwin-Williams, PPG, GM, Ford, Herman Miller and thousands
              of print, textile and paint pros in 60+ countries.
            </p>
          </div>
          <div className="lg:col-span-7 relative grid sm:grid-cols-2 gap-3">
            {USE_CASES.map((u) => (
              <div
                key={u.title}
                className="bg-white/5 hover:bg-white/10 transition-colors rounded-2xl p-5 border border-white/10 flex gap-4 items-start"
              >
                <span
                  className="mt-1 h-3 w-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: u.color }}
                />
                <div>
                  <p className="font-bold">{u.title}</p>
                  <p className="text-sm text-slate-300 mt-1 leading-relaxed">{u.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-pn mt-20 relative">
        <div className="rounded-3xl bg-white border border-slate-100 p-10 md:p-14 shadow-xl overflow-hidden relative">
          <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[#FF6B35]/20 blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">
                Not sure which Nix fits your workflow?
              </p>
              <h2 className="text-3xl md:text-4xl font-black tracking-tight text-[#050F2E]">
                Talk to a color specialist at ProofNation.
              </h2>
              <p className="mt-3 text-slate-600">
                We&apos;ll walk you through Mini, Pro and Spectro side by side, and match you to the
                right instrument, apps and integrations.
              </p>
            </div>
            <div className="flex md:justify-end flex-wrap gap-3">
              <Link to="/contact">
                <Button
                  data-testid="nix-cta-contact"
                  className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-12 px-7 font-bold btn-transition"
                >
                  Get in touch <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
              <Link to="/barbieri">
                <Button
                  data-testid="nix-cta-barbieri"
                  variant="outline"
                  className="rounded-full h-12 px-7 font-bold border-2 border-[#0A2A63] text-[#0A2A63] hover:bg-[#0A2A63] hover:text-white btn-transition"
                >
                  See Barbieri
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default NixPage;
